import { useEffect, useRef, useState, useCallback } from "react";
import type { Cut, TeacherLiveResponse, TeacherLiveRow, WorkResponse, WorkStep } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import {
  TeacherBackLink,
  TeacherMessagePage,
  TeacherModal,
  TeacherQrOverlay,
} from "../components/TeacherChrome";
import { TeacherDemoLauncher } from "../components/TeacherDemoLauncher";
import { TeacherPosterBoard } from "./poster/TeacherPosterBoard";
import { navigate } from "../lib/navigation";
import { useBackClosable } from "@teachermate/shared/back";
import {
  TeacherApiError,
  TeacherLoginRequiredError,
  TeacherRetryableError,
  teacherRequest,
} from "../lib/teacherApi";

interface GiftTarget {
  participantId: string | null;
  name: string;
}

interface PictureHistoryItem {
  asset_id: string;
  created_at: string;
  url: string;
}

type GiftKind = "music_slot" | "image_regen" | "video_grant";

const stateLabels: Record<TeacherLiveRow["state"], string> = {
  joined: "참여",
  writing: "작성 중",
  generating: "생성 중",
  failed: "도움 필요",
  complete: "완성",
};

const stepLabels: Record<WorkStep, string> = {
  mode: "방식 선택",
  lyrics: "가사",
  humming: "허밍",
  style: "스타일",
  result: "노래 생성",
  done: "음악 완성",
  song: "곡 선택",
  story: "스토리",
  cuts: "컷 제작",
  mv_done: "완성",
  edit: "편집",
  final: "영상 완성",
  pic_pick: "그림 고르기",
  pic_image: "그림 완성",
  pic_song: "노래 붙이기",
  pic_done: "작품 완성",
  pst_draw: "그림 만들기",
  pst_edit: "편집하기",
  pst_song: "노래 · AR",
  pst_done: "인쇄 확정",
};

export function TeacherLivePage({ sessionId }: { sessionId: string }) {
  const [data, setData] = useState<TeacherLiveResponse | null>(null);
  const [fatal, setFatal] = useState<"login" | "forbidden" | null>(null);
  const [stale, setStale] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [giftTarget, setGiftTarget] = useState<GiftTarget | null>(null);
  const [giftAmount, setGiftAmount] = useState<1 | 2 | 3 | 5>(5);
  const [giftKind, setGiftKind] = useState<GiftKind>("music_slot");
  const [deleteOpen, setDeleteOpen] = useState(false);
  // 창이 열린 채 뒤로가기 = 창만 닫힘(DESIGN-POLICY §9.H-18 v2.4) — 삭제 뒤 목록으로 가므로 부모가 쥐고 closeThen 을 쓴다.
  const { closeThen: closeDeleteThen } = useBackClosable(deleteOpen, () => setDeleteOpen(false));
  const [qrOpen, setQrOpen] = useState(false);
  const [lyrics, setLyrics] = useState<{ name: string; text: string } | null>(null);
  const [preview, setPreview] = useState<{ name: string; cuts: Cut[]; pictures: PictureHistoryItem[] } | null>(null);
  const [finalVideo, setFinalVideo] = useState<{ name: string; url: string } | null>(null);
  const lyricsTriggerRef = useRef<HTMLElement | null>(null);
  // 학생이 만든 곡 전부(2026-10-06 jery: 여러 곡을 만들어도 교사는 1곡만 들을 수 있었다).
  const [audioByRow, setAudioByRow] = useState<Record<string, Array<{ id: string; label: string; url: string }>>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  // 1초 시계가 다시 그려도 같은 함수 — 자식 진행판의 폴링을 다시 걸지 않게(QA07).
  const handleLoginRequired = useCallback(() => setFatal("login"), []);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;
    let failures = 0;
    const poll = async (): Promise<void> => {
      try {
        const next = await teacherRequest<TeacherLiveResponse>(
          `/api/sessions/${encodeURIComponent(sessionId)}/live`,
        );
        if (cancelled) return;
        setData(next);
        setGiftKind((current) => {
          if (next.session.kind === "music") {
            if (current !== "music_slot") setGiftAmount(5);
            return "music_slot";
          }
          if (current === "music_slot") {
            setGiftAmount(3);
            return "image_regen";
          }
          return current;
        });
        setLastUpdatedAt(Date.now());
        failures = 0;
        setStale(false);
        timer = window.setTimeout(() => void poll(), 5_000);
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof TeacherLoginRequiredError) {
          setFatal("login");
          return;
        }
        if (caught instanceof TeacherApiError && caught.status === 403) {
          setFatal("forbidden");
          return;
        }
        failures += 1;
        if (failures >= 3) setStale(true);
        const delay = Math.min(5_000 * 2 ** Math.max(0, failures - 1), 30_000);
        timer = window.setTimeout(() => void poll(), delay);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [sessionId]);

  if (fatal) return <TeacherMessagePage kind={fatal} />;

  const withBusy = async (key: string, action: () => Promise<void>): Promise<void> => {
    setBusy((current) => ({ ...current, [key]: true }));
    setFeedback((current) => ({ ...current, [key]: "" }));
    try {
      await action();
    } catch (caught) {
      if (caught instanceof TeacherLoginRequiredError) setFatal("login");
      else if (caught instanceof TeacherApiError && caught.status === 403) setFatal("forbidden");
      else setFeedback((current) => ({ ...current, [key]: "다시 시도해 주세요" }));
    } finally {
      setBusy((current) => ({ ...current, [key]: false }));
    }
  };

  const listen = (row: TeacherLiveRow): Promise<void> => withBusy(`listen:${row.participant_id}`, async () => {
    // 옛 서버(tracks 없음)면 고른 곡 한 곡만.
    const tracks = row.tracks?.length ? row.tracks : row.track ? [row.track] : [];
    if (tracks.length === 0) return;
    const players = await Promise.all(tracks.map(async (track, index) => {
      const result = await teacherRequest<{ url: string }>(
        `/api/assets/${encodeURIComponent(track.asset_id)}/url`,
      );
      const chosen = tracks.length > 1 && row.track?.id === track.id ? " · 고른 곡" : "";
      return { id: track.id, label: `${index + 1}곡${chosen}`, url: result.url };
    }));
    setAudioByRow((current) => ({ ...current, [row.participant_id]: players }));
  });

  const regenerate = (row: TeacherLiveRow): Promise<void> => withBusy(`regenerate:${row.participant_id}`, async () => {
    await teacherRequest(`/api/works/${encodeURIComponent(row.work_id)}/regenerate`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({}),
    });
    setData((current) => current
      ? { ...current, rows: current.rows.map((candidate) => candidate.work_id === row.work_id ? { ...candidate, state: "generating" } : candidate) }
      : current);
  });

  const showLyrics = (row: TeacherLiveRow, trigger: HTMLElement): Promise<void> => {
    lyricsTriggerRef.current = trigger;
    return withBusy(`lyrics:${row.participant_id}`, async () => {
      const work = await teacherRequest<WorkResponse>(`/api/works/${encodeURIComponent(row.work_id)}`);
      setLyrics({ name: row.name, text: work.work.lyrics ?? "가사가 없는 허밍 곡이에요." });
    });
  };

  const showPreview = (row: TeacherLiveRow): Promise<void> => withBusy(`preview:${row.participant_id}`, async () => {
    const result = await teacherRequest<{ cuts: Cut[]; pictures?: PictureHistoryItem[] }>(
      `/api/sessions/${encodeURIComponent(sessionId)}/works/${encodeURIComponent(row.work_id)}/cuts`,
    );
    setPreview({ name: row.name, cuts: result.cuts, pictures: result.pictures ?? [] });
  });

  /**
   * 완성 영상 재생. 서명 URL(10분)을 그때그때 받아 모달 <video> 로 튼다 —
   * 컷 미리 보기만 있으면 교사가 «결과물의 본체» 인 완성 영상을 볼 길이 없다(2026-08-18 QA).
   */
  const showFinalVideo = (row: TeacherLiveRow): Promise<void> => withBusy(`final:${row.participant_id}`, async () => {
    if (!row.final_video_asset_id) return;
    const result = await teacherRequest<{ url: string }>(
      `/api/assets/${encodeURIComponent(row.final_video_asset_id)}/url`,
    );
    setFinalVideo({ name: row.name, url: result.url });
  });

  const sendGift = async (): Promise<void> => {
    if (!giftTarget) return;
    const key = giftTarget.participantId ? `gift:${giftTarget.participantId}` : "gift:all";
    await withBusy(key, async () => {
      await teacherRequest(`/api/sessions/${encodeURIComponent(sessionId)}/gift`, {
        method: "POST",
        body: JSON.stringify({
          kind: data?.session.kind === "music" ? "music_slot" : giftKind,
          amount: data?.session.kind === "music" ? 5 : giftAmount,
          ...(giftTarget.participantId
            ? { participant_id: giftTarget.participantId }
            : { all: true }),
        }),
      });
      setFeedback((current) => ({ ...current, [key]: "보냈어요 ✓" }));
      window.setTimeout(() => setFeedback((current) => ({ ...current, [key]: "" })), 2_000);
      setGiftTarget(null);
    });
  };

  /**
   * 수업 삭제. 예전엔 여기 「수업 종료」가 있었는데 **삭제로 갈음한다**(2026-08-11 운영자 결정).
   *
   * 🚨 종료와 범위가 다르다는 걸 화면이 말해야 한다 — 종료는 완성곡·완성영상을 남겼지만
   *    삭제는 남기지 않는다. 그래서 확인 모달이 지워지는 것을 하나하나 적는다.
   *
   * 지운 수업의 상세는 더 이상 존재하지 않으므로 목록으로 되돌린다(replace — 뒤로가기로 시체에
   * 돌아가면 404 를 보게 된다).
   */
  const deleteSession = async (): Promise<void> => {
    await withBusy("delete", async () => {
      await teacherRequest(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
      // 창 칸을 먼저 치운 뒤에 목록으로 바꿔 낀다(closeThen) — 안 그러면 창 칸이 지운 수업 주소로 남는다.
      closeDeleteThen(() => {
        setDeleteOpen(false);
        navigate("/teacher", { replace: true });
      });
    });
  };

  const readonly = data?.session.status === "ended";
  const secondsSinceUpdate = lastUpdatedAt ? Math.max(0, Math.floor((now - lastUpdatedAt) / 1_000)) : 0;
  const mvStepCounts = data?.session.kind === "mv"
    ? {
        song: data.rows.filter((row) => row.step === "song").length,
        story: data.rows.filter((row) => row.step === "story").length,
        cuts: data.rows.filter((row) => row.step === "cuts").length,
        mv_done: data.rows.filter((row) => ["mv_done", "edit", "final"].includes(row.step)).length,
        final: data.rows.filter((row) => row.final_video_completed).length,
      }
    : null;
  // 🔑 그림 수업(2026-09-24) — 표 머리 칸은 단계 네 칸 그대로다.
  const pictureStepCounts = data?.session.kind === "image"
    ? {
        pick: data.rows.filter((row) => row.step === "pic_pick").length,
        image: data.rows.filter((row) => row.step === "pic_image").length,
        song: data.rows.filter((row) => row.step === "pic_song").length,
        done: data.rows.filter((row) => row.step === "pic_done").length,
      }
    : null;
  /** 컷 그림을 쓰는 수업(영상·그림) — 단계 열·그림 보기·그림 선물이 여기에 붙는다. */
  const usesPictures = data?.session.kind === "mv" || data?.session.kind === "image" || data?.session.kind === "poster";
  const isPoster = data?.session.kind === "poster";

  return (
    <main className="teacher-page">
      <div className="teacher-page-inner teacher-live">
        <header className="teacher-page-heading teacher-live-heading">
          <div>
            <TeacherBackLink />
            <span className="teacher-eyebrow">{readonly ? "수업 결과" : "라이브 현황"}</span>
            <h1>{data?.session.title ?? "수업을 불러오는 중..."}</h1>
            {data ? <p>참여 코드 <b>{data.session.code}</b></p> : null}
          </div>
          <div className="teacher-live-header-actions">
            {stale ? <span className="stale-badge">연결이 불안정해요 — 마지막 업데이트 {secondsSinceUpdate}초 전</span> : null}
            {!readonly ? <button className="button-secondary" type="button" onClick={() => setQrOpen(true)}>QR 크게 보기</button> : null}
            {/* 🔑 «끝난 수업» 에서도 보여야 한다 — AR 키링은 수업이 끝난 뒤에 만드는 물건이다. */}
            {data?.session.kind === "mv" ? (
              <button
                className="button-secondary"
                type="button"
                onClick={() => navigate(`/teacher/ar/${encodeURIComponent(sessionId)}`)}
              >
                AR 키링 만들기
              </button>
            ) : null}
            {/* 시연작 진입구는 목록이 아니라 여기다 — DESIGN-POLICY §9.H-14 v1.10(2026-08-07). */}
            {!readonly && data
              ? (
                <TeacherDemoLauncher
                  sessionId={sessionId}
                  onLoginRequired={handleLoginRequired}
                />
              )
              : null}
            {/* 🔑 그림 수업의 감상은 학생 태블릿의 작품 카드와 학교 전시 페이지가 맡는다 —
                 감상회 화면은 곡·영상만 튼다(그림 수업이면 빈 화면이 된다). */}
            {data?.session.kind !== "image" && data?.session.kind !== "poster" ? (
              <button className="button-secondary" type="button" onClick={() => navigate(`/teacher/party/${encodeURIComponent(sessionId)}`)}>감상회</button>
            ) : null}
            <button className="button-danger" type="button" onClick={() => setDeleteOpen(true)}>수업 삭제</button>
          </div>
        </header>

        {/* 🚨 바깥 화면(제목·코드·학생 표)이 아직 옛 반이면 진행판을 그리지 않는다 — 옛 반을 보면서 새 반에 선물하게 된다(Codex 재검수). */}
        {data && isPoster && data.session.id === sessionId ? <TeacherPosterBoard key={sessionId} sessionId={sessionId} onLoginRequired={handleLoginRequired} /> : null}
        {data ? (
          <>
            {isPoster ? null : <section className={`teacher-count-grid${mvStepCounts ? " teacher-count-grid-mv" : ""}`} aria-label="수업 진행 현황">
              {mvStepCounts ? (
                <>
                  <article><span>곡 선택</span><strong>{mvStepCounts.song}</strong></article>
                  <article><span>스토리</span><strong>{mvStepCounts.story}</strong></article>
                  <article><span>컷 제작</span><strong>{mvStepCounts.cuts}</strong></article>
                  <article><span>컷 완성</span><strong>{mvStepCounts.mv_done}</strong></article>
                  <article><span>영상 완성</span><strong>{mvStepCounts.final}</strong></article>
                </>
              ) : pictureStepCounts ? (
                <>
                  <article><span>그림 고르기</span><strong>{pictureStepCounts.pick}</strong></article>
                  <article><span>그림 완성</span><strong>{pictureStepCounts.image}</strong></article>
                  <article><span>노래 붙이기</span><strong>{pictureStepCounts.song}</strong></article>
                  <article><span>작품 완성</span><strong>{pictureStepCounts.done}</strong></article>
                </>
              ) : (
                <>
                  <article><span>참여</span><strong>{data.counts.joined}</strong></article>
                  <article><span>작성 중</span><strong>{data.counts.writing}</strong></article>
                  <article><span>생성 중</span><strong>{data.counts.generating}</strong></article>
                  <article><span>완성</span><strong>{data.counts.complete}</strong></article>
                </>
              )}
            </section>}

            <section className="teacher-table-card">
              <div className="teacher-table-toolbar">
                <h2>학생 {data.rows.length}명</h2>
                {!readonly ? (
                  <div>
                    {feedback["gift:all"] ? <span className="teacher-feedback">{feedback["gift:all"]}</span> : null}
                    <button className="button-secondary" type="button" onClick={() => setGiftTarget({ participantId: null, name: "반 전체" })}>🎁 반 전체 선물</button>
                  </div>
                ) : <span className="readonly-badge">읽기 전용</span>}
              </div>
              {/* 🔑 시연작은 «이 수업 안의 작품» 이다(B1) — 학생 표 «밖에», 이 수업 안에 놓는다.
                   ai-app-builder 의 「내 시범작」 탭과 같은 자리다. 학생 표에 섞으면 교사가
                   자기 시연을 학생으로 착각해 출석을 잘못 읽는다. */}
              {data.demo ? (
                <div className="teacher-demo-work">
                  <div className="teacher-demo-work-info">
                    <span className="teacher-demo-badge">내 시연작</span>
                    {/* 🔑 음악 수업도 «실제 단계» 를 보여준다(2026-08-14 jery QA). 예전에는 영상 수업만
                           stepLabels 를 쓰고 음악은 "진행 중" 을 박아 둬서, 교사가 시연곡을 다 만들어도
                           표시가 영원히 «진행 중» 이었다 — 자기 시연이 끝났는지를 화면이 답하지 못했다. */}
                    <span>{stepLabels[data.demo.step] ?? data.demo.step}</span>
                    <small>
                      {new Date(data.demo.updated_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                    </small>
                  </div>
                  <span className="teacher-demo-work-hint">학생 수에는 세지 않아요</span>
                </div>
              ) : null}
              <div className="teacher-table-scroll">
                <table className="teacher-student-table">
                  <thead>
                    <tr><th>학생</th><th>{usesPictures ? "단계" : "방식"}</th><th>상태</th><th>최근 활동</th><th>조치</th></tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => {
                      const rowBusy = Object.entries(busy).some(([key, value]) => key.endsWith(row.participant_id) && value);
                      const listening = busy[`listen:${row.participant_id}`] ?? false;
                      const loadingLyrics = busy[`lyrics:${row.participant_id}`] ?? false;
                      const regenerating = busy[`regenerate:${row.participant_id}`] ?? false;
                      const loadingPreview = busy[`preview:${row.participant_id}`] ?? false;
                      const loadingFinal = busy[`final:${row.participant_id}`] ?? false;
                      return (
                        <tr className={row.state === "failed" ? "needs-help" : ""} key={row.participant_id}>
                          <td>
                            <strong>{row.name}</strong>{row.flags.length ? <span className="flag-badge">검토 {row.flags.length}</span> : null}
                            {/* 곡마다 노래 코드(2026-10-12 jery) — 학생이 코드를 잃어버려도 강사가 불러 줄 수 있다. */}
                            {data.session.kind === "music" && row.tracks?.some((track) => track.song_code) ? (
                              <ul className="teacher-song-codes">
                                {row.tracks.filter((track) => track.song_code).map((track) => (
                                  <li key={track.id}><span>{track.title}</span> <code>{track.song_code}</code></li>
                                ))}
                              </ul>
                            ) : null}
                          </td>
                          <td>{usesPictures ? stepLabels[row.step] : row.mode === "humming" ? "허밍" : row.mode === "lyrics" ? "가사" : "대기"}</td>
                          <td><span className={`student-state ${row.state}`}>{stateLabels[row.state]}</span>{data.session.kind === "mv" && row.cut_count > 0 ? <small>{row.completed_cuts}/{row.cut_count}컷</small> : null}{row.fail_count > 0 ? <small>연속 실패 {row.fail_count}회</small> : null}</td>
                          <td>{new Date(row.last_activity_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</td>
                          <td>
                            <div className="teacher-row-actions">
                              {usesPictures && !isPoster ? (
                                <button type="button" disabled={rowBusy} aria-busy={loadingPreview} onClick={() => void showPreview(row)}>
                                  {loadingPreview
                                    ? <InlineSpinner label="불러오는 중..." />
                                    : data.session.kind === "image" ? "그림 보기" : "컷 미리 보기"}
                                </button>
                              ) : row.track ? (
                                <button type="button" disabled={rowBusy} aria-busy={listening} onClick={() => void listen(row)}>
                                  {listening ? <InlineSpinner label="듣는 중..." /> : (row.tracks?.length ?? 0) > 1 ? `들어보기 (${row.tracks!.length}곡)` : "들어보기"}
                                </button>
                              ) : null}
                              {/* 🚨 button-primary 를 쓰면 안 된다 — `.teacher-row-actions button` 이 배경을
                                   흰색으로 덮어써 흰 글자만 남는다(hover 때만 보이던 사고, 2026-08-18). */}
                              {data.session.kind === "mv" && row.final_video_asset_id ? (
                                <button type="button" className="row-action-primary" disabled={rowBusy} aria-busy={loadingFinal} onClick={() => void showFinalVideo(row)}>
                                  {loadingFinal ? <InlineSpinner label="불러오는 중..." /> : "완성 영상"}
                                </button>
                              ) : null}
                              {data.session.kind === "music" ? <button type="button" disabled={rowBusy} aria-busy={loadingLyrics} onClick={(event) => void showLyrics(row, event.currentTarget)}>
                                {loadingLyrics ? <InlineSpinner label="불러오는 중..." /> : "가사 보기"}
                              </button> : null}
                              {!readonly && data.session.kind === "music" ? (
                                <>
                                  <button type="button" disabled={rowBusy} aria-busy={regenerating} onClick={() => void regenerate(row)}>
                                    {regenerating ? <InlineSpinner label="요청 중..." /> : "다시 만들어주기"}
                                  </button>
                                  <button type="button" disabled={rowBusy} onClick={() => setGiftTarget({ participantId: row.participant_id, name: row.name })}>🎁 선물</button>
                                </>
                              ) : null}
                              {!readonly && usesPictures ? (
                                <button type="button" disabled={rowBusy} onClick={() => setGiftTarget({ participantId: row.participant_id, name: row.name })}>🎁 선물</button>
                              ) : null}
                            </div>
                            {audioByRow[row.participant_id]?.map((player) => (
                              <div className="teacher-inline-track" key={player.id}>
                                {audioByRow[row.participant_id]!.length > 1 ? <small>{player.label}</small> : null}
                                <audio className="teacher-inline-audio" controls preload="none" src={player.url} />
                              </div>
                            ))}
                            {feedback[`gift:${row.participant_id}`] ? <span className="teacher-feedback">{feedback[`gift:${row.participant_id}`]}</span> : null}
                            {Object.entries(feedback).find(([key, value]) => key.endsWith(row.participant_id) && value === "다시 시도해 주세요") ? <span className="row-error-toast">다시 시도해 주세요</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : <p className="teacher-empty">학생 현황을 불러오는 중...</p>}
      </div>

      {giftTarget ? (
        <TeacherModal
          title={`${giftTarget.name}에게 ${usesPictures ? "만들기 기회" : "생성 기회"} 선물`}
          onClose={() => setGiftTarget(null)}
          actions={(
            <>
              <button className="button-secondary" type="button" onClick={() => setGiftTarget(null)}>취소</button>
              <button className="button-primary" type="button" disabled={busy[giftTarget.participantId ? `gift:${giftTarget.participantId}` : "gift:all"]} onClick={() => void sendGift()}>선물 보내기</button>
            </>
          )}
        >
          <p>학생 화면에 선물 알림이 표시되고 이번 수업의 남은 횟수가 바로 늘어나요.</p>
          <div className="gift-amount-picker">
            {usesPictures ? (
              <>
                <button type="button" className={giftKind === "image_regen" && giftAmount === 3 ? "selected" : ""} onClick={() => {
                  setGiftKind("image_regen");
                  setGiftAmount(3);
                }}>그림 +3회</button>
                <button type="button" className={giftKind === "image_regen" && giftAmount === 5 ? "selected" : ""} onClick={() => {
                  setGiftKind("image_regen");
                  setGiftAmount(5);
                }}>그림 +5회</button>
                {(data?.session.kind === "mv" || data?.session.kind === "poster" || data?.session.kind === "image") && data.session.video_enabled ? (
                  <>
                    <button type="button" className={giftKind === "video_grant" && giftAmount === 1 ? "selected" : ""} onClick={() => {
                      setGiftKind("video_grant");
                      setGiftAmount(1);
                    }}>+AI영상 1회</button>
                    <button type="button" className={giftKind === "video_grant" && giftAmount === 2 ? "selected" : ""} onClick={() => {
                      setGiftKind("video_grant");
                      setGiftAmount(2);
                    }}>+AI영상 2회</button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {/* 🔑 곡 선물은 +5곡 하나뿐이다(2026-10-18 jery · 기본 10곡 수업과 짝). */}
                <button type="button" className="selected" onClick={() => setGiftAmount(5)}>5곡</button>
              </>
            )}
          </div>
        </TeacherModal>
      ) : null}

      {deleteOpen ? (
        <TeacherModal
          title="수업을 삭제할까요?"
          backClosable={false}
          onClose={() => setDeleteOpen(false)}
          destructive
          actions={(
            <>
              <button className="button-secondary" type="button" data-teacher-modal-cancel onClick={() => setDeleteOpen(false)}>그대로 두기</button>
              <button className="button-danger" type="button" disabled={busy.delete} onClick={() => void deleteSession()}>{busy.delete ? "삭제 중..." : "삭제"}</button>
            </>
          )}
        >
          {/* 🚨 「무엇이 사라지는가」를 뭉뚱그리지 않는다 — 되돌릴 수 없는 조치라 각오할 거리를 준다. */}
          <p><b>{data?.session.title}</b> 수업을 완전히 지워요. 되돌릴 수 없어요.</p>
          <p>{data?.session.kind === "mv"
            ? "참여 학생 명단, 만든 컷 그림과 올린 음악, 완성한 영상까지 모두 사라져요."
            : data?.session.kind === "image"
              ? "참여 학생 명단, 만든 그림과 붙인 노래까지 모두 사라져요. 학교 전시 페이지에서도 빠져요."
              : "참여 학생 명단, 허밍 녹음, 완성한 노래까지 모두 사라져요."
          }</p>
        </TeacherModal>
      ) : null}

      {lyrics ? (
        <TeacherModal
          title={`${lyrics.name}의 가사`}
          onClose={() => setLyrics(null)}
          returnFocusRef={lyricsTriggerRef}
        >
          <pre className="teacher-lyrics-preview">{lyrics.text}</pre>
        </TeacherModal>
      ) : null}

      {preview ? (
        <TeacherModal title={`${preview.name}의 ${data?.session.kind === "image" ? "그림" : "컷 미리 보기"}`} onClose={() => setPreview(null)}>
          <div className="teacher-cut-preview">
            {preview.cuts.map((cut) => (
              <article key={cut.id}>
                {cut.image_url ? <img src={cut.image_url} alt={`컷 ${cut.idx}: ${cut.scene_text}`} /> : <div>그림 대기</div>}
                <strong>컷 {cut.idx}</strong>
                <p>{cut.scene_text}</p>
              </article>
            ))}
          </div>
          {/* 다시 그리기 전 그림까지 전부(023 이후 남는다 · 2026-10-06 jery). 지금 그림 한 장뿐이면 생략. */}
          {preview.pictures.length > 1 ? (
            <section className="teacher-picture-history" aria-label="만든 그림 전부">
              <strong>만든 그림 전부 ({preview.pictures.length}장)</strong>
              <div className="teacher-cut-preview">
                {preview.pictures.map((picture, index) => (
                  <article key={picture.asset_id}>
                    <img src={picture.url} alt={`${preview.name}의 ${index + 1}번째 그림`} loading="lazy" />
                    <strong>{index + 1}번째</strong>
                    <p>{new Date(picture.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </TeacherModal>
      ) : null}

      {finalVideo ? (
        <TeacherModal title={`${finalVideo.name}의 완성 영상`} onClose={() => setFinalVideo(null)}>
          {/* 서명 URL 은 10분짜리라 저장하지 않는다 — 모달을 다시 열면 새로 받는다. */}
          <video className="teacher-final-video" controls autoPlay src={finalVideo.url} />
        </TeacherModal>
      ) : null}

      {qrOpen && data ? (
        <TeacherQrOverlay
          title={data.session.title}
          code={data.session.code}
          participantCount={data.rows.length}
          onClose={() => setQrOpen(false)}
        />
      ) : null}
    </main>
  );
}
